class FakeUrl {
    /**
     * Build the fake `URL`.
     *
     * @param {string} protocol The URL protocol.
     * @param {string} username  The URL username.
     * @param {string} password The URL password.
     * @param {string} host The URL host.
     * @param {string} hostname The URL hostname.
     * @param {string} port The URL port.
     * @param {string} pathname The URL pathname.
     * @param {string} search  The URL search (a.k.a the query string).
     * @param {string} hash The URL hash (a.k.a the fragment).
     * @constructor
     */
    constructor(protocol, username, password, host, hostname, port, pathname, search, hash) {
        this.protocol = protocol;
        this.username = username || '';
        this.password = password || '';
        this.host = host || null;
        this.hostname = hostname || null;
        this.port = port || null;
        this.pathname = pathname || '';
        this.search = search || '';
        this.hash = hash || '';

        // Ensure the pathname always starts with a '/'
        if (this.pathname[0] !== '/') {
            this.pathname = '/' + this.pathname;
        }
    }
}

/**
 * Parse URL using native API.
 *
 * @param {string} url The URL.
 * @return {FakeUrl} The parsed URL.
 */
function nativeUrl(url) {
    const result = new URL(url);
    return new FakeUrl(
        result.protocol,
        result.username,
        result.password,
        result.host,
        result.hostname,
        result.port,
        result.pathname,
        result.search,
        result.hash,
    );
}

/**
 * Parse URL using polyfill url parser.
 *
 * @param {string} url The URL.
 * @return {FakeUrl} The parsed URL.
 */
function polyfillUrl(url) {
    const location = window.location;
    const a = document.createElement('a');
    a.href = url;

    // Be careful, in IE11, it may be empty if not specified in the `href` attribute, in this case, use values from
    // current window.location, as it should be the default.
    const protocol = a.protocol || location.protocol;
    const username = a.username || location.username;
    const password = a.password || location.password;
    const host = a.host || location.host;
    const hostname = a.hostname || location.hostname;
    const port = a.port || location.port;

    return new FakeUrl(protocol, username, password, host, hostname, port, a.pathname, a.search, a.hash);
}

/**
 * Ensure that given URL is valid with expected values.
 *
 * @param {URL} url The URL.
 * @param {string} protocol The expected protocol part.
 * @param {string} hostname The expected hostname part.
 * @param {string} port The expected port part.
 * @param {string} pathname The expected pathname part.
 * @param {string} search The expected search part.
 * @return {boolean} `true` if `url` has expected settings, `false` otherwise.
 */
function ensureUrl(url, protocol, hostname, port, pathname, search) {
    return (
        url !== null &&
        url.protocol === protocol &&
        url.hostname === hostname &&
        url.port === port &&
        url.host === `${hostname}:${port}` &&
        url.pathname === pathname &&
        url.search === search
    );
}

/**
 * Check for `URL` support in current environment.
 *
 * @return {boolean} `true` if `URL` supported, `false` otherwise.
 */
function checkUrlSupport() {
    try {
        const url = new URL('http://localhost:8080/test?q') !== null;
        return ensureUrl(url, 'http:', 'localhost', '8080', '/test', '?q');
    } catch (e) {
        return false;
    }
}

const toUrl = checkUrlSupport() ? nativeUrl : polyfillUrl;

/**
 * Parse given URL to a new `URL` instance, returns `null` if URL parsing fails.
 *
 * @param {RequestInfo} url The URL.
 * @return {FakeUrl} The parsed URL.
 */
export function parseUrl(url) {
    if (!url) {
        return null;
    }

    try {
        return toUrl(url);
    } catch (e) {
        return null;
    }
}
